import { DownloadSimple } from "@phosphor-icons/react"
import { Button, type ButtonProps } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { AccessibilityText } from "@tokenterminal/ui/AccessibilityText"
import { Checkbox, Input, Label } from "@tokenterminal/ui/Input"
import { RadioTabs } from "@tokenterminal/ui/Input/RadioTabs"
import { Stack, type StackProps } from "@tokenterminal/ui/Stack"
import { theme } from "@tokenterminal/ui/theme"
import { formatDate } from "@tokenterminal/ui/utils"
import { useTracking } from "components/Tracking"
import { sendEvent } from "modules/analytics"
import {
  ACCESS_ACTIONS,
  ACCESS_COMPONENTS,
  useAccess,
} from "modules/auth/useAccess"
import { getCohortAnalysisData } from "modules/CohortAnalysis/datasource"
import { getFundraisesData } from "modules/Fundraises/datasource"
import { getInsiderTransactionData } from "modules/InisiderTransactions/datasource"
import { getMartData } from "modules/MartDataGrid"
import { LazyProPaywallModal } from "modules/paywall/LazyProPaywallModal"
import { getProjectContractsData } from "modules/ProjectContracts/datasource"
import { getCryptoScreenerData } from "modules/ProjectsDataGrid/datasource"
import { getStableCoinsData } from "modules/Stablecoins/datasource"
import { getTrendingContractsData } from "modules/TrendingContracts/datasource"
import {
  type FormEventHandler,
  type ReactNode,
  useCallback,
  useState,
} from "react"
import {
  type MartFetcher,
  type MartFetcherOptions,
  type MartId,
} from "types/ApiTypes"
import { type ExportType, useDataGrid } from "./DataGridContext"
import { LoadingCog } from "./Loading"
import { getColumnNames } from "./utils/getColumnNames"
import type { ColDef, ColGroupDef } from "./DataGrid"
import CheckIcon from "@/icons/check.svg"
import CrossIcon from "@/icons/close.svg"
import CogIcon from "@/icons/cog.svg"

type OnDownload = (action: () => Promise<void>) => void

const showCols = (col: ColDef<any, any>) => ({
  ...col,
  hide: false,
  initialHide: false,
})

const syncInitialHide = (col: ColDef<any, any>) => ({
  ...col,
  /*
   * Hide is only ever true or undefined
   * Grid mounts with "initialHide" -> affects "hide", user does selections -> affects "hide"
   * Take "hide" and sync to "initialHide" -> export Grid uses synced "initialHide"
   *
   * If we don't sync, then export Grid mounts with whatever was default in the first place
   */
  initialHide: col.hide === true ? true : false,
})

type ColumnDef = ColDef<any, any> | ColGroupDef<any>

function isColGroup(columnDef: ColumnDef): columnDef is ColGroupDef {
  return (
    (columnDef as ColGroupDef).children &&
    Array.isArray((columnDef as ColGroupDef).children)
  )
}

const ShouldRender = ({
  if: renderIfTrue,
  children,
}: {
  if: boolean
  children: ReactNode
}) => (renderIfTrue ? children : null)

const makeAllColsShown = (cols: ColumnDef[]) =>
  cols.map((col) =>
    isColGroup(col)
      ? { ...col, children: col.children.map(showCols) }
      : showCols(col)
  )

const syncColsHideAndInitialHide = (cols: ColumnDef[]) =>
  cols.map((col) =>
    isColGroup(col)
      ? { ...col, children: col.children.map(syncInitialHide) }
      : syncInitialHide(col)
  )

interface ResolveColConfig {
  showAll: boolean
  noIndexColumn: boolean
}

const resolveColDefs = (
  cols: ColumnDef[],
  { showAll, noIndexColumn }: ResolveColConfig
): ColumnDef[] => {
  let colDefs = showAll
    ? makeAllColsShown(cols)
    : syncColsHideAndInitialHide(cols)
  if (noIndexColumn) {
    colDefs = colDefs.filter((col) => (col as any).type !== "rowIndexColumn")
  }
  return colDefs
}

// TODO: refactor to use the generic fetcher somehow - IN PROGRESS: Still needs some way to pass the correct body to getMartData
const martFetchers: { [key in MartId]: MartFetcher } = {
  insider_transactions: getInsiderTransactionData,
  fundraises: getFundraisesData,
  project_contracts: getProjectContractsData,
  trending_contracts: getTrendingContractsData,
  stablecoins: getStableCoinsData,
  crypto_screener: getCryptoScreenerData,
  cohort_analysis: getCohortAnalysisData,
}

interface DownloadState {
  isLoading?: boolean
  progressText?: string
  errorText?: string
}

const labelTextClassNames = "text-sm text-fg-secondary mb-2"

const Ribbon = ({ isError = false }) => (
  <div
    className={`flex gap-2 items-center justify-between rounded ${isError ? "bg-red-500/10" : "bg-neutral-500/10"} p-6`}
  >
    {isError ? (
      <CrossIcon width="20" />
    ) : (
      <CheckIcon width="20" color={theme.colors.primary.background} />
    )}
  </div>
)

const ExportOverlay = ({
  children,
  opacity = 0.75,
  ...stackProps
}: StackProps & { opacity?: number }) => (
  <Stack
    alignItems="center"
    textAlign="center"
    justifyContent="center"
    {...stackProps}
  >
    {children}
  </Stack>
)

const InProgressOverlay = ({
  errorText,
  progressText,
  isLoading,
}: DownloadState) => {
  return (
    <ExportOverlay>
      {isLoading ? <LoadingCog /> : <Ribbon isError={!!errorText} />}
      {errorText ? (
        <div className="text-sm font-medium text-fg-red">{errorText}</div>
      ) : (
        <div className="text-sm font-medium">{progressText}</div>
      )}
    </ExportOverlay>
  )
}

const exportTypeOptions = [
  { value: "csv", label: "CSV" },
  { value: "excel", label: "Excel" },
]

type ExportMode = "export-view" | "export-dataset"

const ExportModalContent = ({
  onDownload,
  cancel,
  defaultExportMode,
  hideVisibleColumnsCheck = false,
  exportDatasetTitle = "Export dataset",
}: {
  onDownload: OnDownload
  cancel: () => void
  defaultExportMode?: null | ExportMode
  hideVisibleColumnsCheck?: boolean
  exportDatasetTitle?: string
}) => {
  const {
    martId,
    getColDefs,
    createExporter,
    downloadCsv,
    downloadExcel,
    getState,
  } = useDataGrid()
  const [{ isLoading, progressText, errorText }, setState] =
    useState<DownloadState>({})
  const [exportMode, setExportMode] = useState<ExportMode | undefined>(
    defaultExportMode ? defaultExportMode : undefined
  )
  const { contextValue } = useTracking()
  const exportTitle =
    exportMode === "export-dataset" ? exportDatasetTitle : `Export what you see`

  const onSubmitExport: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault()
      // Callback triggers paywall if not allowed. User still gets to see how stuff would work and what would be available if they would pay
      onDownload(async () => {
        if (e.currentTarget) {
          try {
            setState({ isLoading: true, progressText: "Fetching data" })
            const formData = new FormData(e.currentTarget)

            const fileType = formData.get("fileType") as ExportType

            if (exportMode === "export-view") {
              // Early exit
              switch (fileType) {
                case "csv":
                  downloadCsv()
                  break
                case "excel":
                  downloadExcel()
                  break
              }

              const state = getState()
              sendEvent("download", {
                component: "datatable",
                format: fileType,
                name: "current",
                filters: {
                  columns: getColumnNames(
                    state?.columns ? (state.columns as Array<ColumnDef>) : []
                  ),
                  filters: state?.filters,
                },
                page_type: contextValue.pageType!,
                page_id: contextValue.pageId!,
              })
              setState({ isLoading: false, progressText: "Success!" })
              setTimeout(() => {
                setState({})
              }, 500)
              return
            }

            const fetcherOptions: MartFetcherOptions = {
              limit: Number(formData.get("rowLimit")) || -1,
              offset: Number(formData.get("rowOffset")) || undefined,
              // Make sure exporting data is never grouped, because that will suppress all data
              rowGroupCols: [],
              groupKeys: [],
            }

            // Construct filename as [tt-dataset]_[date]_[params-used]
            let fileName = `tt-${martId.replace(/_/g, "-")}_${formatDate(
              new Date()
            )}`
            if (fetcherOptions.limit !== -1 || fetcherOptions.offset) {
              if (!fetcherOptions.offset) {
                fileName = `${fileName}_first-${fetcherOptions.limit}-rows`
              } else if (fetcherOptions.limit === -1) {
                fileName = `${fileName}_from-row-${fetcherOptions.offset}`
              } else if (fetcherOptions.limit !== -1 && fetcherOptions.offset) {
                fileName = `${fileName}_rows-between-${fetcherOptions.offset}-and-${fetcherOptions.limit}`
              }
            }

            let martFetcher: MartFetcher

            try {
              martFetcher =
                martFetchers[martId] ??
                (async (...args) => getMartData(martId, ...args))
            } catch (error) {
              console.error("Dataset missing fetcher", error)
              throw "Failed to fetch export data"
            }

            const dataP = martFetcher(undefined, fetcherOptions)
            const exporterP = createExporter(fileType, { fileName })
            const colDefs = getColDefs()! // Cant do much if there's no columns

            const columnDefs = resolveColDefs(colDefs, {
              noIndexColumn: formData.get("noIndexColumn") !== null,
              showAll: formData.get("filteredColumns") !== "yes",
            })

            const [data, exporter] = await Promise.all([dataP, exporterP])

            if (!data.length) {
              // Possible if dataset is eg. 100 rows and user uses offset of 120
              throw "No data available with given parameters."
            }

            sendEvent("download", {
              component: "datatable",
              format: fileType,
              name: "full",
              filters: {
                columns: getColumnNames(columnDefs),
                limit: fetcherOptions.limit,
                offset: fetcherOptions.offset ?? 0,
              },
              page_type: contextValue.pageType!,
              page_id: contextValue.pageId!,
            })

            setState({
              isLoading: true,
              progressText: `Generating ${fileType}`,
            })
            exporter({ data, columnDefs }, () => {
              setState({ isLoading: false, progressText: "Success!" })
              setTimeout(() => {
                setState({})
              }, 500)
            })
          } catch (error) {
            console.error(`Export failed`, error)
            setState({
              isLoading: false,
              errorText:
                typeof error === "string" ? error : "Something went wrong!",
            })
            setTimeout(() => {
              setState({})
            }, 1500)
          }
        }
      })
    },
    [
      onDownload,
      exportMode,
      martId,
      createExporter,
      getColDefs,
      contextValue.pageType,
      contextValue.pageId,
      getState,
      downloadCsv,
      downloadExcel,
    ]
  )

  return (
    <>
      <form
        onSubmit={onSubmitExport}
        className="flex flex-col gap-6 relative"
        style={{
          minWidth: "min(540px, 85vw)",
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        <ShouldRender if={typeof exportMode === "undefined"}>
          <ExportOverlay
            opacity={1}
            gap="l"
            textAlign="left"
            alignItems="flexStart"
          >
            <div className="flex flex-col gap-6 w-full p-6">
              <div className="flex w-full items-center justify-between">
                <Heading as="h2" level={3} className="flex gap-2">
                  <CogIcon width="1em" />
                  What do you want to export?
                </Heading>
                <Button type="reset" className="self-end" onPress={cancel}>
                  {isLoading ? "Cancel" : "Close"}
                </Button>
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Button
                  className="w-full justify-start !h-auto"
                  onPress={() => {
                    setExportMode("export-view")
                  }}
                  size="large"
                >
                  <div className="flex flex-col gap-1">
                    <Heading as="p" level={4}>
                      What you see
                    </Heading>
                    <div className="text-fg-secondary text-sm">
                      Export only what you see currently in your browser.
                    </div>
                  </div>
                </Button>
                <Button
                  className="w-full justify-start !h-auto"
                  onPress={() => {
                    setExportMode("export-dataset")
                  }}
                  size="large"
                >
                  <div className="flex flex-col gap-1">
                    <Heading as="p" level={4}>
                      Full data
                    </Heading>
                    <div className="text-fg-secondary text-sm">
                      Export everything from this dataset. Data filtering
                      won&apos;t apply.
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          </ExportOverlay>
        </ShouldRender>
        <ShouldRender if={typeof exportMode !== "undefined"}>
          <div className="flex flex-col gap-6 py-6">
            <div className="flex flex-col gap-2 px-6 pb-4 w-full border-b border-solid border-border-default">
              <div className="flex w-full items-center justify-between">
                <Heading as="h2" level={3}>
                  {exportTitle}
                </Heading>
                <Button type="reset" className="self-end" onPress={cancel}>
                  {isLoading ? "Cancel" : "Close"}
                </Button>
              </div>
              <div
                className="text-fg-secondary text-sm"
                style={{ maxWidth: "40ch" }}
              >
                {exportMode === "export-dataset"
                  ? `Configure exported data.`
                  : `Select file type for exported data.`}
              </div>
            </div>
            <div className="flex flex-col gap-6 px-6 relative">
              <fieldset>
                <legend className={labelTextClassNames}>Export type</legend>
                <RadioTabs
                  options={exportTypeOptions}
                  defaultValue="csv"
                  name="fileType"
                />
              </fieldset>
              <ShouldRender if={exportMode === "export-dataset"}>
                <fieldset>
                  <AccessibilityText as="legend">Rows</AccessibilityText>
                  <div className="flex gap-2 items-center justify-between">
                    <Label flex="1 1 auto">
                      <label className={labelTextClassNames}>Row limit</label>
                      <Input
                        id="row-limit"
                        name="rowLimit"
                        type="number"
                        placeholder="Eg. 1000"
                      />
                    </Label>
                    <Label style={{ flex: "1 1 50px" }}>
                      <label className={labelTextClassNames}>Offset</label>
                      <Input
                        id="row-offset"
                        name="rowOffset"
                        type="number"
                        placeholder="0"
                      />
                    </Label>
                  </div>
                </fieldset>
                <ShouldRender if={!hideVisibleColumnsCheck}>
                  <fieldset>
                    <AccessibilityText as="legend">Columns</AccessibilityText>
                    <div className="flex gap-2 items-center justify-between">
                      <Checkbox
                        name="filteredColumns"
                        id="filtered-columns"
                        value="yes"
                      >
                        Include visible columns only
                      </Checkbox>
                      <Checkbox
                        name="noIndexColumn"
                        id="skip-index"
                        value="yes"
                        defaultChecked
                      >
                        Exclude # column
                      </Checkbox>
                    </div>
                  </fieldset>
                </ShouldRender>
              </ShouldRender>
              {errorText || progressText ? (
                <InProgressOverlay
                  errorText={errorText}
                  progressText={progressText}
                  isLoading={isLoading}
                />
              ) : null}
            </div>
            <div className="flex w-full justify-end px-6">
              <Button type="submit" loading={isLoading} variant="solid">
                Download
              </Button>
            </div>
          </div>
        </ShouldRender>
      </form>
    </>
  )
}

export const DataGridExportButton = (
  props: Omit<ButtonProps, "onClick" | "variant" | "size" | "children">
) => {
  const { contextValue } = useTracking()
  const { martId } = useDataGrid()
  const [isProModalOpen, setIsProModalOpen] = useState(false)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)

  const hasDownloadAccess = useAccess(
    ACCESS_COMPONENTS.DATASETS,
    ACCESS_ACTIONS.DOWNLOAD
  )

  const onDownload: OnDownload = useCallback(
    (download: () => void) => {
      if (hasDownloadAccess) {
        download()
      } else {
        setIsProModalOpen(true)

        sendEvent("wall", {
          action: "download",
          wall_type: "pro",
          component: "datatable",
          name: martId,
          filters: {},
          page_id: contextValue.pageId,
          page_type: contextValue.pageType!,
        })
      }
    },
    [hasDownloadAccess, martId, contextValue.pageId, contextValue.pageType]
  )

  return (
    <>
      <Button
        size="xsmall"
        variant="muted"
        className="!text-xs "
        onPress={() => setIsDownloadModalOpen(true)}
        {...props}
      >
        <Icon as={DownloadSimple} />
        Export
      </Button>
      <Modal
        isOpen={isDownloadModalOpen}
        onOpenChange={setIsDownloadModalOpen}
        size="auto"
      >
        <ExportModalContent
          onDownload={onDownload}
          cancel={() => setIsDownloadModalOpen(false)}
        />
      </Modal>
      <LazyProPaywallModal
        type="download_chart"
        featureName="Downloading chart data"
        onClose={() => setIsProModalOpen(false)}
        isOpen={isProModalOpen}
        setIsOpen={setIsProModalOpen}
      />
    </>
  )
}
